import type { UseQueryResult } from '@tanstack/react-query';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { Pathname } from '~/shared/http-fetcher/http-fetcher';

import type { GlobalSearchJobLevelResponse } from '../../../data-contract/global-search';
import { globalSearchJobLevelResponseSchema, globalSearchTermSchema } from '../../../data-contract/global-search';

const DEFAULT_PAGE_SIZE = 20;

type UseGlobalSearchJobLevelsOptions = {
    readonly enabled?: boolean;
    readonly page?: number;
    readonly searchTerm: string;
    readonly size?: number;
};

export const useGlobalSearchJobLevels = ({
    enabled = true,
    page = 0,
    searchTerm,
    size = DEFAULT_PAGE_SIZE,
}: UseGlobalSearchJobLevelsOptions): UseQueryResult<GlobalSearchJobLevelResponse> => {
    const { applicationState, validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    const pathname: Pathname = '/global/search/job-levels';

    const searchParams = {
        page,
        searchTerm,
        size,
    };

    return useQuery({
        enabled: enabled && globalSearchTermSchema.safeParse(searchTerm).success,
        placeholderData: keepPreviousData,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(globalSearchJobLevelResponseSchema, {
                pathname,
                requestOptions: { searchParams },
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        throwOnError: false,
        queryKey: [pathname, 'GET', applicationState.language.code, searchParams],
    });
};
